<template>
    <div class="user">
        <v-container>
            <v-layout row wrap v-if="user">
                <v-flex mt-4 ml-4 xs12 class="grey--text text--darken-1">
                    <h1 class="heading">User Console</h1>
                </v-flex>
             
                <v-flex pa-4 xs12 sm6 class="grey--text text--darken-1">
                    <v-card flat class="mr-5 my-3 pa-2 grey--text text--darken-3" v-if="user">
                        <v-card-text class="grey--text text--darken-3 mx-auto">
                            <div v-if="!user.acceptedTerms && !user.email" class="grey--text text--darken-3 mx-auto">
                                <h2 class="grey--text text--darken-3 mb-4">Want to log in without Telegram?</h2>
                                <p>Register your email!
                                    You'll also be able to subscribe to our newsletter for project
                                    updates, and in the near future, set up alerts for your ground station.</p>
                            </div>
              
                            <template>
                                <div>
                                    <v-btn color="primary" @click="showEmailDialog = true" class="station-action-btn">e-mail</v-btn>
               
                                    <v-btn color="primary" @click="showCredentialsDialog = true"
                                        class="ml-2 station-action-btn">CREDENTIALS</v-btn>
                                  
                                    <v-btn color="primary" @click="showNewStationDialog = true" class="ml-2 station-action-btn">
                                        NEW STATION
                                    </v-btn>

                                    <EmailPreferencesDialog
                                        v-model="showEmailDialog"
                                        :initial-email="user.email"
                                        :initial-email-validated="user.emailValidated"
                                        :initial-pre-email="user.email"
                                        :initial-accepted-terms="user.acceptedTerms"
                                        :initial-preacepted-terms="user.acceptedTerms"
                                        :initial-newsletter-frequency="user.newsletterFrequency"
                                        :timer-active="timerActive"
                                        :timer-countdown="timerCountdown"
                                        @submit="handleEmailSubmit"
                                    />

                                    <MqttCredentialsDialog
                                        :value="showCredentialsDialog"
                                        @input="showCredentialsDialog = $event"
                                        @dialog-closed="handleCredentialsDialogClose"
                                    />
                                
                                    <NewStationDialog
                                        :value="showNewStationDialog"
                                        @input="showNewStationDialog = $event"
                                        @station-creation-started="getStations"
                                        @station-creation-success="handleStationCreationSuccess"
                                        @station-creation-error="handleStationCreationError"
                                    />
                                </div>
                            </template>

                            <div v-if="!user.emailValidated && user.acceptedTerms" class="red--text mt-2">
                                Please check your mailbox and validate the email.
                            </div>
                        </v-card-text>
                    </v-card>
                </v-flex>
                <v-flex xs12 sm12 pa-4>
                    <div v-for="station in stations" :key="`${station.name}@${station.userId}`">
                        <Station :station="station" />
                        <v-divider></v-divider>
                    </div>
                </v-flex>
            </v-layout>
            <v-row v-else class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="grey"></v-progress-circular>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import { ref, onBeforeMount, onMounted, onUnmounted } from 'vue';
import axios from '../plugins/axios';
import Station from '../components/Station.vue';
import EmailPreferencesDialog from '../components/user/EmailPreferencesDialog.vue';
import MqttCredentialsDialog from '../components/user/MqttCredentialsDialog.vue';
import NewStationDialog from '../components/user/NewStationDialog.vue';

export default {
    name: 'User',
    components: {
        Station,
        EmailPreferencesDialog,
        MqttCredentialsDialog,
        NewStationDialog
    },
    
    setup(props, { emit }) {
        const user = ref(null);
        const stations = ref(null);
        // Use debounced dialog state
        const showEmailDialogRaw = ref(false);
        const showCredentialsDialog = ref(false);
        const showNewStationDialog = ref(false);

        // Timer state
        const timerActive = ref(false);
        const timerCountdown = ref(0);
        let timerInterval = null;
        let saveTimerDebounce = null;
        let lastSavedState = null;

        // Debounce utility
        function debounce(fn, delay) {
            let timeout;
            return function (...args) {
                clearTimeout(timeout);
                timeout = setTimeout(() => fn.apply(this, args), delay);
            };
        }

        // Validate loaded timer state
        function validateTimerState(obj) {
            if (!obj || typeof obj !== 'object') return false;
            if (!('expiresAt' in obj)) return false;
            if (typeof obj.expiresAt !== 'number' || obj.expiresAt < 0) return false;
            // expiresAt should not be more than 24h in the future
            if (obj.expiresAt - Date.now() > 24 * 3600 * 1000) return false;
            return true;
        }

        // Load timer state from localStorage with error handling and validation
        function loadTimerState() {
            try {
                const saved = localStorage.getItem('emailDialogTimer');
                if (saved) {
                    let parsed;
                    try {
                        parsed = JSON.parse(saved);
                    } catch {
                        localStorage.removeItem('emailDialogTimer');
                        return;
                    }
                    if (!validateTimerState(parsed)) {
                        localStorage.removeItem('emailDialogTimer');
                        return;
                    }
                    const now = Date.now();
                    if (parsed.expiresAt && parsed.expiresAt > now) {
                        timerCountdown.value = Math.ceil((parsed.expiresAt - now) / 1000);
                        timerActive.value = true;
                        startTimerInterval();
                    } else {
                        clearTimer();
                    }
                }
            } catch (e) {
                // Fallback: just clear timer state if localStorage fails
                clearTimer();
            }
        }

        // Debounced save to localStorage
        const debouncedSaveTimerState = debounce(() => {
            try {
                if (timerActive.value) {
                    const expiresAt = Date.now() + timerCountdown.value * 1000;
                    const state = { expiresAt };
                    if (JSON.stringify(state) !== lastSavedState) {
                        localStorage.setItem('emailDialogTimer', JSON.stringify(state));
                        lastSavedState = JSON.stringify(state);
                    }
                } else {
                    localStorage.removeItem('emailDialogTimer');
                    lastSavedState = null;
                }
            } catch (e) {
                // Ignore localStorage errors
            }
        }, 5000);

        // Save timer state on significant changes (start/stop/reset)
        function saveTimerState(immediate = false) {
            if (immediate) {
                try {
                    if (timerActive.value) {
                        const expiresAt = Date.now() + timerCountdown.value * 1000;
                        const state = { expiresAt };
                        if (JSON.stringify(state) !== lastSavedState) {
                            localStorage.setItem('emailDialogTimer', JSON.stringify(state));
                            lastSavedState = JSON.stringify(state);
                        }
                    } else {
                        localStorage.removeItem('emailDialogTimer');
                        lastSavedState = null;
                    }
                } catch (e) {
                    // Ignore localStorage errors
                }
            } else {
                debouncedSaveTimerState();
            }
        }

        // Start timer
        function startTimer() {
            timerCountdown.value = 60;
            timerActive.value = true;
            saveTimerState(true); // immediate save on start
            startTimerInterval();
        }

        // Timer interval logic with debounced save
        function startTimerInterval() {
            if (timerInterval) clearInterval(timerInterval);
            timerInterval = setInterval(() => {
                if (timerCountdown.value > 0) {
                    timerCountdown.value -= 1;
                    saveTimerState(); // debounced save
                }
                if (timerCountdown.value <= 0) {
                    clearTimer();
                }
            }, 1000);
        }

        // Clear timer
        function clearTimer() {
            timerActive.value = false;
            timerCountdown.value = 0;
            if (timerInterval) {
                clearInterval(timerInterval);
                timerInterval = null;
            }
            saveTimerState(true); // immediate save on stop/reset
        }

        // Cleanup on unmount
        onUnmounted(() => {
            if (timerInterval) {
                clearInterval(timerInterval);
                timerInterval = null;
            }
            if (saveTimerDebounce) clearTimeout(saveTimerDebounce);
        });

        // Debounced dialog open/close
        // Removed duplicate showEmailDialogRaw and debounced dialog logic

        // Load timer on mount
        onMounted(() => {
            loadTimerState();
        });

        const handleCredentialsDialogClose = () => {
            showCredentialsDialog.value = false;
        };

        const getStations = async () => {
            if (!localStorage.userId) return;
            try {
                const { data } = await axios.get(`/v1/stations?userId=${localStorage.userId}`);
                stations.value = data;
            } catch (error) {
                console.error('Failed to fetch stations:', error);
            }
        };

        const getUser = async () => {
            if (!localStorage.userId || !localStorage.sessionToken) {
                user.value = null;
                return;
            }

            const config = {
                headers: {
                    sessionToken: localStorage.sessionToken,
                    userId: localStorage.userId
                }
            };

            try {
                const { data } = await axios.get(
                    `/v1/user?userId=${localStorage.userId}`,
                    config
                );

                user.value = {
                    email: data.email || null,
                    newsletterFrequency: data.newsletterFrequency || 'none',
                    acceptedTerms: data.acceptedTerms || false,
                    emailValidated: data.emailVal || false
                };
            } catch (error) {
                user.value = null;
            }
        };

        const handleEmailSubmit = async (formData) => {
            const config = {
                headers: {
                    sessionToken: localStorage.sessionToken,
                    userId: localStorage.userId
                }
            };

            try {
                await axios.put(`/v1/user/${localStorage.userId}`, formData, config);
                emit("configSaved", formData);

                user.value = {
                    ...user.value,
                    email: formData.email,
                    acceptedTerms: formData.acceptedTerms,
                    newsletterFrequency: formData.newsletterFrequency
                };

                showEmailDialogRaw.value = false;
                alert("Config saved!"); // Consider replacing with a less disruptive notification

                // Start timer after successful submit
                startTimer();
            } catch (err) {
                console.error('Failed to save email preferences:', err);
            }
        };

        const handleStationCreationSuccess = () => {
            getStations(); // Refresh stations list
        };

        const handleStationCreationError = (errorMessage) => {
            console.error('Station creation failed:', errorMessage);
        };

        onBeforeMount(() => {
            getStations();
            getUser();
        });

        return {
            user,
            stations,
            showEmailDialog: showEmailDialogRaw,
            showCredentialsDialog,
            showNewStationDialog,
            handleCredentialsDialogClose,
            getStations,
            getUser,
            handleEmailSubmit,
            handleStationCreationSuccess,
            handleStationCreationError,
            timerActive,
            timerCountdown
        };
    }
}
</script>

<style>
/* Styles remain the same */
.station-action-btn {
  margin-bottom: 12px !important;
}
</style>
